import i18next from 'i18next';
import jwtDecode from 'jwt-decode';
import { isUndefined, omit } from 'lodash';
import type { Project, ProjectOverviewProps } from 'src/types/project';
import axios from 'src/utils/axios';
import getId from 'src/utils/getId';
import logger from 'src/utils/logger';
import parse from 'src/utils/parse';

class ProjectApi {
  async create(project: Project): Promise<Project> {
    return new Promise((resolve, reject) => {
      let prop = 'submit';
      delete project[prop];
      if (project?.client_id === 0) {
        prop = 'client_id';
        delete project[prop];
      }
      if (project?.service_client_id === 0) {
        prop = 'service_client_id';
        delete project[prop];
      }
      // Create Project
      axios.post(`${process.env.REACT_APP_PROJECTS_URL}`, project).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        }
      }).catch((err) => {
        logger(err, 'error');
        reject(err);
      });
    });
  }

  async edit(project: Project): Promise<Project> {
    return new Promise((resolve, reject) => {
      let prop = '';
      const props = ['submit', 'client', 'serviceClient'];
      project = omit(project, props);
      delete project[prop];
      if (project?.client_id === 0) {
        prop = 'client_id';
        delete project[prop];
      }
      if (project?.service_client_id === 0) {
        prop = 'service_client_id';
        delete project[prop];
      }
      // Update Project
      axios.put(`${process.env.REACT_APP_PROJECTS_URL}/${project?.project_id}`, project).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        }
      }).catch((err) => {
        logger(err, 'error');
        reject(err);
      });
    });
  }

  async getAll(page: number, limit: number, currentTab: number, search: string, sortColumn: any, sortDirection: boolean): Promise<ProjectOverviewProps> {
    return new Promise((resolve, reject) => {
      // Get By Id Project
      let sortList = {};
      if (sortColumn.name) {
        sortList = {
          'project.name': sortDirection ? 'ASC' : 'DESC',
        };
      }

      if (sortColumn.batch_number) {
        sortList = {
          'project.batch_number': sortDirection ? 'ASC' : 'DESC',
        };
      }

      if (sortColumn.client) {
        sortList = {
          'client.name': sortDirection ? 'ASC' : 'DESC',
          'client.email': sortDirection ? 'ASC' : 'DESC',
        };
      }

      if (sortColumn.total_devices) {
        sortList = {
          deviceTotalCount: sortDirection ? 'ASC' : 'DESC',
        };
      }

      if (sortColumn.project_is_activated) {
        sortList = {
          'project.is_activated': sortDirection ? 'ASC' : 'DESC',
        };
      }

      if (sortColumn.created_at) {
        sortList = {
          'project.created_at': sortDirection ? 'ASC' : 'DESC',
        };
      }

      const filter: any = {
        page,
        limit,
        sortList
      };

      let data: any;

      if (Object.keys(sortList)?.length === 0) {
        const sortListProp = 'sortList';
        delete filter[sortListProp];
      }

      if (search?.length > 0) {
        data = {
          Filter: filter,
          search
        };
      } else if (currentTab === 2) {
        data = {
          Filter: filter,
          unassigned_projects: true,
        };
      } else {
        data = {
          Filter: filter,
        };
      }

      if (!isUndefined(currentTab) && currentTab >= 0 && currentTab < 2) {
        filter.is_activated = !!currentTab;
      }

      axios.post(`${process.env.REACT_APP_PROJECTS_URL}/getAllProjects`, data).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const res = !isUndefined(response?.data?.totalCount) ? response?.data : response?.data?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async createProjectSetting(projectSetting: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const prop = 'submit';
      delete projectSetting[prop];
      // Create Project Setting
      axios.post(`${process.env.REACT_APP_PROJECT_SETTINGS_URL}`, projectSetting).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        }
      }).catch((err) => {
        logger(err, 'error');
        reject(err);
      });
    });
  }

  async editProjectSetting(projectSetting: any, settingId: number): Promise<Project> {
    return new Promise((resolve, reject) => {
      const prop = 'submit';
      delete projectSetting[prop];
      // Update Project Setting
      axios.put(`${process.env.REACT_APP_PROJECT_SETTINGS_URL}/${settingId}`, projectSetting).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        }
      }).catch((err) => {
        logger(err, 'error');
        reject(err);
      });
    });
  }

  async getAllProjectSetting(page: number, limit: number, projectId: number): Promise<ProjectOverviewProps> {
    return new Promise((resolve, reject) => {
      // Get All Project Setting

      const filter: any = {
        page,
        limit,
      };

      const data: any = {
        Filter: filter,
        project_id: projectId
      };

      axios.post(`${process.env.REACT_APP_PROJECT_SETTINGS_URL}/getAllProjectSetting`, data).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const res = !isUndefined(response?.data?.totalCount) ? response?.data : response?.data?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async getById(projectId: number): Promise<Project> {
    return new Promise((resolve, reject) => {
      // Get By Id Project
      axios.get(`${process.env.REACT_APP_PROJECTS_URL}/${projectId}`).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async setMbusHourlyLogStatus(should_save_mbus_hourly_logs: boolean, projectId: number): Promise<Project> {
    return new Promise((resolve, reject) => {
      // set-mbus-hourly-log-status
      axios.patch(`${process.env.REACT_APP_PROJECTS_URL}/set-mbus-hourly-log-status/${projectId}`, {
        should_save_mbus_hourly_logs
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async getAllProjectAutoComplete(search: string): Promise<Project[]> {
    return new Promise((resolve, reject) => {
      const currentUrl = window.location.href;
      const payload: any = { search };
      if (currentUrl.includes('monitoring') || currentUrl.includes('/audit') || currentUrl.includes('/mbus_reporting') || currentUrl.includes('/mbus_reporting_snoozed_device') || currentUrl.includes('/mbus_missing_hourly_reporting') || currentUrl.includes('/inconsistent_mbus_devices_reporting') || currentUrl.includes('/pre_payment_reporting')) {
        payload.unclaimed_devices = true;
      }
      // Get By Id User
      axios.post(`${process.env.REACT_APP_PROJECTS_URL}/getAutoComplete`, payload).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const res:any = response?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async getProjectsByClientId(clientId, search: string): Promise<Project[]> {
    return new Promise((resolve, reject) => {
      // Get Projects By ClientId
      axios.get(`${process.env.REACT_APP_SUBSCRIPTION_URL}/getProjectsOfClient/${clientId}?search=${search}`).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const res:any = response;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async deActivate(projectId: string, isActive: boolean): Promise<Project> {
    return new Promise((resolve, reject) => {
      // De-active Project
      axios.patch(`${process.env.REACT_APP_PROJECTS_URL}/delete/${projectId}`, {
        is_activated: !isActive
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async bulkDeActivate(projectIds: string[]): Promise<Project> {
    return new Promise((resolve, reject) => {
      // Bulk De-active Projects
      axios.post(`${process.env.REACT_APP_PROJECTS_URL}/bulk_deactive`, {
        list: projectIds
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async bulkDelete(projectIds: string[]): Promise<Project> {
    return new Promise((resolve, reject) => {
      // Bulk De-active Projects
      axios.post(`${process.env.REACT_APP_USERS_URL}/bulkDeleteProject`, {
        list: projectIds
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async downloadProjectTable(t: any, currentTab: number, search: string, selectedProjects: any, permissions: any = {}): Promise<ProjectOverviewProps> {
    const tabs: any = {};
    let filters: any = {
      Filter: {
      },
    };

    if (search?.length > 0) {
      filters = {
        Filter: {},
        search
      };
    } else if (currentTab === 2) {
      filters = {
        Filter: {},
        unassigned_projects: true,
      };
    } else {
      filters = {
        Filter: {},
      };
    }

    if (!isUndefined(currentTab) && currentTab >= 0 && currentTab <= 2) {
      filters.is_activated = !!currentTab;
    }

    if (selectedProjects?.length > 0) {
      filters.idList = selectedProjects;
    }

    return new Promise((resolve, reject) => {
      // Download Device Table
      const fieldList: any = {};

      if (permissions?.isFortesAdmin) {
        fieldList.client_id = t('projects_form_client_field');
        fieldList.main_client_contact_id = t('projects_form_main_client_contact_field');
      }

      if (permissions?.isFortesAdmin || permissions?.isClientAdmin) {
        fieldList.service_client_id = t('projects_form_service_client_field');
        fieldList.main_service_client_contact_id = t('projects_form_main_service_client_contact_field');
      }

      Object.assign(fieldList, {
        name: t('projects_form_project_name_field'),
        project_order_number: t('projects_form_project_order_number_field'),
        description: t('projects_form_description_field'),
        deviceTotalCount: t('inconsistent_mbus_devices_reporting_table_total_devices'),
        active_devices_count: t('projects_table_online_devices'),
        projects_status: t('projects_table_status'),
        projects_active: t('projects_active'),
        projects_inactive: t('projects_inactive'),
        created_at: t('projects_table_added'),
      });

      const decoded: any = jwtDecode(localStorage.getItem('accessToken'));
      const roles = decoded?.realm_access?.roles;
      const isFortesUserRegex = new RegExp('^fortes_');
      const isFortesUser = roles?.find((role) => (
        isFortesUserRegex?.test(role)
      ));

      if (isFortesUser) {
        fieldList.total_amount_order = t('projects_form_total_amount_order_field');
      }
      const data = {
        fieldList,
        ...filters,
        search,
        ...tabs,
      };
      axios({
        url: `${process.env.REACT_APP_PROJECTS_URL}/download`,
        responseType: 'blob',
        method: 'post',
        data,
      }).then((response) => {
        if (!isUndefined(response?.data)) {
          const res = response?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async getProjectDetailsByIdsList(list): Promise<any> {
    return new Promise((resolve, reject) => {
      // Bulk De-active Devices
      axios.post(`${process.env.REACT_APP_PROJECTS_URL}/getProjectIdsList`, {
        list
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          const data = response?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(new Error(i18next.t('internal_server_error')));
      });
    });
  }

  async postBaseTopic(baseTopic: any, projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // postBaseTopic
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/set-base-topic-by-project`, {
        project_id: projectId,
        topic: baseTopic,
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async deviceV2Reboot(projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // deviceV2Reboot
      axios.get(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/project-reboot/${projectId}`).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async postSecondaryDeviceType(deviceType: number, projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // postSecondaryDeviceType
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/set-secondary-device-type-by-projectId`, {
        project_id: projectId,
        type: deviceType,
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async postModbusBaudRate(baudRate: number, projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // postModbusBaudRate
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/set-modbus-baud-rate-by-projectId`, {
        project_id: projectId,
        baud_rate: baudRate,
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async postModbusErrorRegisterAddress(register: number, projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // postModbusErrorRegisterAddress
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/set-modbus-error-register-address-by-projectId`, {
        project_id: projectId,
        register,
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async postModbusExpectedErrorRegisterValue(register: number, projectId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      // postModbusExpectedErrorRegisterValue
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/mqtt_worker/set-modbus-error-register-value-by-projectId`, {
        project_id: projectId,
        value: register,
      }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data?.data)) {
          const data = response?.data?.data;
          resolve(data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async exportProjectDeviceTable(): Promise<any> {
    const filters: any = {
      Filter: {
      },
    };

    if (window.location.href?.indexOf('project') !== -1) {
      filters.project_id = parseInt(getId(), 10);
    }

    return new Promise((resolve, reject) => {
      // Download Device Table
      const data = {
        fieldList: {
          device_name: 'device_name',
          serial_number: 'serial_number',
          street_number: 'street_number',
          house_number: 'house_number',
          postal_code: 'postal_code',
          neighbourhood: 'neighbourhood',
          floor: 'floor',
          construction_number: 'construction_number',
          city: 'city',
          country: 'country',
        },
        ...filters,
      };
      axios({
        url: `${process.env.REACT_APP_DEVICES_URL}/project/download`,
        responseType: 'blob',
        method: 'post',
        data,
      }).then((response) => {
        if (!isUndefined(response?.data)) {
          const res = response?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async getProjectsWithMissingMainContact(clientId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios.get(`${process.env.REACT_APP_PROJECTS_URL}/getProjectsWithMissingMainContact/${clientId}`).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response?.data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async updateMainClientContact(projectList: { project_id: number; main_client_contact_id: number }[]): Promise<any> {
    return new Promise((resolve, reject) => {
      axios.put(`${process.env.REACT_APP_PROJECTS_URL}/updateMainClientContact`, { projectList }).then((response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response?.data);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }

  async uploadProjectCommissioningFile(event: string): Promise<any> {
    // Reset Password
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', event);
      axios.post(`${process.env.REACT_APP_DEVICES_URL}/project/import`, formData).then((response) => {
        response = parse(response);
        if (!isUndefined(response)) {
          const res = response?.data;
          resolve(res);
        } else {
          reject(new Error(i18next.t('invalid_server_response')));
        }
      }).catch((error) => {
        logger(error, 'error');
        reject(error);
      });
    });
  }
}

export const projectApi = new ProjectApi();
